import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SynergyMapping, SynergyEffects, ResponseConditions } from '../../types/ScenarioManagement';
import { useTheme } from '../../contexts/ThemeContext';
import { StandardButton, StandardCard, FormField, TextAreaField } from '../ui';
import { investments as globalInvestments } from '../../data/investments';

interface SynergyEditorProps {
  synergies: SynergyMapping[];
  selectedInvestmentIds: string[];
  selectedIncidentIds: string[];
  onChange: (synergies: SynergyMapping[]) => void;
}

interface SynergyFormData {
  id: string;
  name: string;
  description: string;
  requiredInvestmentIds: string[];
  effects: SynergyEffects;
  conditions?: ResponseConditions;
  enabled: boolean;
  priority: number;
}

const SynergyEditor: React.FC<SynergyEditorProps> = ({
  synergies,
  selectedInvestmentIds,
  selectedIncidentIds,
  onChange
}) => {
  const { isDark } = useTheme();
  const [editingSynergy, setEditingSynergy] = useState<SynergyFormData | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Get available investments
  const availableInvestments = globalInvestments.filter(inv => 
    selectedInvestmentIds.includes(inv.id)
  );

  const createNewSynergy = (): SynergyFormData => ({
    id: `synergy-${Date.now()}`,
    name: '',
    description: '',
    requiredInvestmentIds: [],
    effects: {
      responseMultipliers: { security: 1, reputation: 1, financial: 1, operational: 1 },
      responseBonuses: { security: 0, reputation: 0, financial: 0, operational: 0 },
      impactMitigation: 0,
      timeReduction: 0
    },
    enabled: true,
    priority: 5
  });

  const handleAddSynergy = () => {
    setEditingSynergy(createNewSynergy());
    setShowForm(true);
  };

  const handleEditSynergy = (synergy: SynergyMapping) => {
    setEditingSynergy({
      ...synergy,
      enabled: synergy.enabled ?? true,
      priority: synergy.priority ?? 5,
      effects: {
        responseMultipliers: synergy.effects.responseMultipliers || { security: 1, reputation: 1, financial: 1, operational: 1 },
        responseBonuses: synergy.effects.responseBonuses || { security: 0, reputation: 0, financial: 0, operational: 0 },
        impactMitigation: synergy.effects.impactMitigation || 0,
        timeReduction: synergy.effects.timeReduction || 0,
        customScoring: synergy.effects.customScoring
      }
    });
    setShowForm(true);
  };

  const handleDeleteSynergy = (id: string) => {
    onChange(synergies.filter(s => s.id !== id));
  };

  const handleSaveSynergy = () => {
    if (!editingSynergy || !editingSynergy.name.trim()) return;

    const isNew = !synergies.find(s => s.id === editingSynergy.id);
    
    if (isNew) {
      onChange([...synergies, editingSynergy]);
    } else {
      onChange(synergies.map(s => s.id === editingSynergy.id ? editingSynergy : s));
    }

    setEditingSynergy(null);
    setShowForm(false);
  };

  const handleCancel = () => {
    setEditingSynergy(null);
    setShowForm(false);
  };

  const updateEffectValue = (
    category: 'responseMultipliers' | 'responseBonuses',
    field: 'security' | 'reputation' | 'financial' | 'operational',
    value: number
  ) => {
    if (!editingSynergy) return;
    
    setEditingSynergy({
      ...editingSynergy,
      effects: {
        ...editingSynergy.effects,
        [category]: {
          ...(editingSynergy.effects[category] || {}),
          [field]: value
        }
      }
    });
  };

  const formatPercentage = (value: number): string => {
    return value > 0 ? `+${Math.round(value * 100)}%` : `${Math.round(value * 100)}%`;
  };

  const getInvestmentName = (id: string): string => {
    return availableInvestments.find(inv => inv.id === id)?.name || id;
  };

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'security': return '🛡️';
      case 'reputation': return '⭐';
      case 'financial': return '💰';
      case 'operational': return '⚙️';
      default: return '📊';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Investment Synergies
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Define how investments enhance response effectiveness
          </p>
        </div>
        <StandardButton
          onClick={handleAddSynergy}
          variant="primary"
          size="small"
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          Add Synergy
        </StandardButton>
      </div>

      {/* Synergy List */}
      {synergies.length === 0 ? (
        <StandardCard variant="outlined" padding="large">
          <div className="text-center py-8">
            <div className="text-5xl mb-4">🤝</div>
            <p className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No Synergies Defined
            </p>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Add synergies to create strategic investment combinations
            </p>
            <StandardButton onClick={handleAddSynergy} variant="primary" size="small">
              Create First Synergy
            </StandardButton>
          </div>
        </StandardCard>
      ) : (
        <div className="space-y-3">
          {synergies.map(synergy => (
            <StandardCard key={synergy.id} variant="outlined" padding="medium">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {synergy.name}
                    </h4>
                    {!synergy.enabled && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {synergy.description}
                  </p>
                  
                  {/* Required Investments */}
                  <div className="mb-3">
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      Required Investments:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {synergy.requiredInvestmentIds.map(id => (
                        <span key={id} className={`text-xs px-2 py-1 rounded-lg ${
                          isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {getInvestmentName(id)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Effects Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {Object.entries(synergy.effects.responseBonuses || {}).map(([category, bonus]) => {
                      if (bonus === 0) return null;
                      return (
                        <div key={category} className="flex items-center gap-1">
                          <span>{getCategoryIcon(category)}</span>
                          <span className={`capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {category}:
                          </span>
                          <span className={`font-medium ${
                            bonus > 0 
                              ? isDark ? 'text-green-400' : 'text-green-600'
                              : isDark ? 'text-red-400' : 'text-red-600'
                          }`}>
                            {formatPercentage(bonus / 100)}
                          </span>
                        </div>
                      );
                    })}
                    {(synergy.effects.impactMitigation || 0) > 0 && (
                      <div className="flex items-center gap-1">
                        <span>🛡️</span>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          Mitigation:
                        </span>
                        <span className={`font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          {formatPercentage((synergy.effects.impactMitigation || 0) / 100)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <StandardButton
                    onClick={() => handleEditSynergy(synergy)}
                    variant="secondary"
                    size="small"
                  >
                    Edit
                  </StandardButton>
                  <StandardButton
                    onClick={() => handleDeleteSynergy(synergy.id)}
                    variant="danger"
                    size="small"
                  >
                    Delete
                  </StandardButton>
                </div>
              </div>
            </StandardCard>
          ))}
        </div>
      )}

      {/* Edit Form Modal */}
      <AnimatePresence>
        {showForm && editingSynergy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={handleCancel}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
                isDark ? 'bg-gray-900' : 'bg-white'
              }`}
            >
              <div className={`sticky top-0 z-10 p-6 border-b ${
                isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {editingSynergy.id.startsWith('synergy-') ? 'Create New Synergy' : 'Edit Synergy'}
                </h3>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <FormField
                    label="Synergy Name"
                    value={editingSynergy.name}
                    onChange={(e) => setEditingSynergy({ ...editingSynergy, name: e.target.value })}
                    placeholder="e.g., Cyber Insurance Claims Process"
                    required
                  />

                  <TextAreaField
                    label="Description"
                    value={editingSynergy.description}
                    onChange={(e) => setEditingSynergy({ ...editingSynergy, description: e.target.value })}
                    placeholder="Describe how these investments work together..."
                    rows={2}
                    required
                  />
                </div>

                {/* Required Investments */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Required Investments (all must be selected for synergy to activate)
                  </label>
                  <div className="space-y-2">
                    {availableInvestments.map(investment => (
                      <label key={investment.id} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingSynergy.requiredInvestmentIds.includes(investment.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditingSynergy({
                                ...editingSynergy,
                                requiredInvestmentIds: [...editingSynergy.requiredInvestmentIds, investment.id]
                              });
                            } else {
                              setEditingSynergy({
                                ...editingSynergy,
                                requiredInvestmentIds: editingSynergy.requiredInvestmentIds.filter(
                                  id => id !== investment.id
                                )
                              });
                            }
                          }}
                          className="w-4 h-4 text-professional-blue-600"
                        />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {investment.icon} {investment.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Response Bonuses */}
                <div>
                  <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Response Effectiveness Bonuses (%)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {(['security', 'reputation', 'financial', 'operational'] as const).map(category => (
                      <div key={category}>
                        <label className={`block text-xs font-medium mb-1 capitalize ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {getCategoryIcon(category)} {category}
                        </label>
                        <input
                          type="number"
                          min="-100"
                          max="100"
                          value={editingSynergy.effects.responseBonuses?.[category] || 0}
                          onChange={(e) => updateEffectValue('responseBonuses', category, parseInt(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? 'bg-gray-800 border-gray-700 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Effects */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Impact Mitigation (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={editingSynergy.effects.impactMitigation}
                      onChange={(e) => setEditingSynergy({
                        ...editingSynergy,
                        effects: {
                          ...editingSynergy.effects,
                          impactMitigation: parseInt(e.target.value) || 0
                        }
                      })}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark
                          ? 'bg-gray-800 border-gray-700 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Priority (1-10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={editingSynergy.priority}
                      onChange={(e) => setEditingSynergy({
                        ...editingSynergy,
                        priority: parseInt(e.target.value) || 5
                      })}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark
                          ? 'bg-gray-800 border-gray-700 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>

                {/* Advanced Conditions */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Custom Condition (optional)
                  </label>
                  <input
                    type="text"
                    value={editingSynergy.conditions?.customCondition || ''}
                    onChange={(e) => setEditingSynergy({
                      ...editingSynergy,
                      conditions: {
                        ...editingSynergy.conditions,
                        customCondition: e.target.value
                      }
                    })}
                    placeholder='e.g., response.label contains "insurance|claim"'
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Use keywords to match response options containing specific terms
                  </p>
                </div>

                {/* Enabled Toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="synergy-enabled"
                    checked={editingSynergy.enabled}
                    onChange={(e) => setEditingSynergy({ ...editingSynergy, enabled: e.target.checked })}
                    className="w-4 h-4 text-professional-blue-600"
                  />
                  <label htmlFor="synergy-enabled" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Enable this synergy
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className={`sticky bottom-0 p-6 border-t flex justify-end gap-3 ${
                isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <StandardButton onClick={handleCancel} variant="secondary">
                  Cancel
                </StandardButton>
                <StandardButton onClick={handleSaveSynergy} variant="primary">
                  Save Synergy
                </StandardButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SynergyEditor;